import { FunctionComponent } from "react";

import { format } from "date-fns";
import { Link } from "@mui/material";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ensureHttpProtocol } from "@/utils/url";

import { ListPerson } from "../../schema/list";
import { CrmIcon } from "../../components/CrmIcon";

/**
 * CRM-sync status badges rendered inside the leads-table Name column: one {@link CrmIcon} per linked
 * CRM prospect, each with a hover tooltip summarising sync status, account owner and any associated
 * deals. Extracted from `EditableNameColumn` in `allColumns.tsx` to keep that file under the
 * file-length ratchet.
 */
export const NameColumnCrmBadges: FunctionComponent<{ row: ListPerson }> = ({ row }) => (
	<>
		{row.prospect?.crmProspects?.map((crmProspect) => (
			<Tooltip
				key={crmProspect.id}
				content={
					<div className="flex flex-col gap-2">
						<span>Current Status: {crmProspect.currentStatus}</span>
						{crmProspect.lastUpdatedAt && (
							<span>
								Last Updated At: {format(new Date(crmProspect.lastUpdatedAt), "HH:mm dd.MM.yyyy")}
							</span>
						)}
						<span>Account Owner: {crmProspect.accountOwner}</span>
						<span>Account Owner ID: {crmProspect.accountOwnerId}</span>
						<span>Reference ID: {crmProspect.referenceId}</span>
						{crmProspect.syncStatus && <span>Sync Status: {crmProspect.syncStatus}</span>}
						{crmProspect.syncError && <span>Sync Error: {crmProspect.syncError}</span>}
						{crmProspect.customField1 && <span>Custom Field 1: {crmProspect.customField1}</span>}
						{crmProspect.customField2 && <span>Custom Field 2: {crmProspect.customField2}</span>}
						{row.prospect?.crmDeals && row.prospect.crmDeals.length > 0 && (
							<>
								<hr className="border-t border-gray-500 my-1" />
								<span className="font-semibold">Deals</span>
								{row.prospect.crmDeals.map((deal) => (
									<div key={deal.id} className="flex flex-col gap-1 ml-2">
										<span className="font-medium">
											{deal.url ? (
												<Link className="text-inherit underline" href={deal.url} target="_blank">
													{deal.dealName}
												</Link>
											) : (
												deal.dealName
											)}
										</span>
										{deal.pipeline && <span>Pipeline: {deal.pipeline}</span>}
										{deal.dealStage && <span>Stage: {deal.dealStage}</span>}
										{deal.dealOwner && <span>Owner: {deal.dealOwner}</span>}
										{deal.closeDate && (
											<span>Close Date: {format(new Date(deal.closeDate), "dd.MM.yyyy")}</span>
										)}
										{deal.hubspotSyncStatus && <span>Sync Status: {deal.hubspotSyncStatus}</span>}
									</div>
								))}
							</>
						)}
					</div>
				}
				disabled={!crmProspect.url && !crmProspect.syncStatus && !crmProspect.syncError}
			>
				{crmProspect.url ? (
					<Link href={ensureHttpProtocol(crmProspect.url)} target="_blank">
						<CrmIcon
							crmType={crmProspect.crmType}
							hasDeal={(row.prospect?.crmDeals?.length ?? 0) > 0}
							syncError={crmProspect.syncError}
							syncStatus={crmProspect.syncStatus}
							url={crmProspect.url ?? ""}
						/>
					</Link>
				) : (
					<CrmIcon
						crmType={crmProspect.crmType}
						hasDeal={(row.prospect?.crmDeals?.length ?? 0) > 0}
						syncError={crmProspect.syncError}
						syncStatus={crmProspect.syncStatus}
						url={crmProspect.url ?? ""}
					/>
				)}
			</Tooltip>
		))}
	</>
);
